/**
 * The Przegląd tab's „Rozliczenie" band (T-K1): four reads, one projection, and the two acts it
 * opens — `features/credit-ledger`'s, the same dialog and the same write the Rozliczenia tab uses.
 *
 * ── Whether the band exists is decided once ─────────────────────────────────────────────────────
 * `model/creditBand`'s `creditBandPresence`, over the booking policy and the credit summary. The
 * policy is read by the route's loader before the screen's first frame, so the wait holds the
 * band's room exactly when credits run — a cold entry moves nothing when the summary lands. The
 * summary still decides once it answers. The ledger (for its start) and the client's calendar
 * ahead (for the next session) are read only once the band stands, and a DISABLED query reads
 * `isPending` for ever — so the gate carries the presence, not the flags alone.
 *
 * A retry after a failed read is a wait too (`isError && isFetching`), as the roster's is — but the
 * band's own, not the tab's: re-reading the credits does not put the whole tab back behind bars.
 *
 * ── An open act outlives the band ───────────────────────────────────────────────────────────────
 * If credits stop running while an act's dialog is open (a refusal's re-read says so), the band
 * leaves, and the draft is closed and said to have been refused, rather than left to reopen when
 * credits come back.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  customerScheduleQueryOptions,
  customerScheduleRoot,
  scheduleWindow,
} from '~/entities/calendar-event';
import {
  CREDIT_REFUSAL_KEYS,
  bookingPolicyQueryOptions,
  creditSummaryQueryOptions,
  customerLedgerPagesQueryOptions,
  useLedgerAct,
  type LedgerActState,
} from '~/features/credit-ledger';
import { addDays, toIsoDate } from '~/shared/lib/calendarDate';

import { toUpcomingSource } from '../api/clientDetailSource';
import { creditBandPresence, creditBandView, type CreditBandView } from '../model/creditBand';

/** How far ahead „Nadchodzące" looks: two months holds the next session of any live plan. */
const AHEAD_DAYS = 60;

export interface CreditBandState {
  /** Any of the band's reads still open the first time — the tab's gate waits for it. */
  readonly pending: boolean;
  /** The band's reads again after a failure: the band waits, the tab does not. */
  readonly rereading: boolean;
  /** „Nadchodzące" again after a failure. */
  readonly rereadingNext: boolean;
  /** Whether the band's room is held while the tab waits. */
  readonly reserved: boolean;
  readonly view: CreditBandView;
  readonly retry: () => void;
  /** „Nadchodzące" did not answer: read the client's calendar again. */
  readonly retrySchedule: () => void;
  readonly act: LedgerActState;
}

interface Read {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly errorUpdateCount: number;
}

/** A read still open for the first time. */
function opening(read: Read): boolean {
  return read.isPending && read.errorUpdateCount === 0;
}

/**
 * A read open again after it failed. A read with no data goes back to `pending` while it is
 * retried (TanStack Query 5 clears the error), so a failure behind it is told by its count.
 */
function reopening(read: Read): boolean {
  return read.isFetching && (read.isError || (read.isPending && read.errorUpdateCount > 0));
}

export function useCreditBand(
  clientId: number,
  announce: (message: string) => void,
): CreditBandState {
  const { t, i18n } = useTranslation();
  const policy = useQuery(bookingPolicyQueryOptions());
  const summary = useQuery(creditSummaryQueryOptions());
  const summaryOpen = opening(summary);
  const presenceInput = {
    customerId: clientId,
    policyOn: policy.data?.creditsEnabled ?? null,
    summary: summaryOpen ? undefined : (summary.data ?? null),
  };
  const presence = creditBandPresence(presenceInput);
  const ready = presence === 'ready';
  const row = summary.data?.clients?.find((entry) => entry.customerId === clientId);

  const ledger = useInfiniteQuery({
    ...customerLedgerPagesQueryOptions(clientId),
    enabled: ready,
  });
  const today = new Date();
  const schedule = useQuery({
    ...customerScheduleQueryOptions(
      clientId,
      scheduleWindow(toIsoDate(today), toIsoDate(addDays(today, AHEAD_DAYS))),
      { enabled: ready },
    ),
    // Midnight moves the window: the day before stays on screen while the new one is read — for
    // THIS client only. `‹ Poprzednia / Następna ›` changes the client under a mounted tab, and
    // the last client's next session must never stand in for this one's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[customerScheduleRoot.length] === clientId ? previous : undefined,
  });

  const act = useLedgerAct(
    clientId,
    { balance: row?.balance ?? 0, sessionsOwed: row?.sessionsOwed ?? 0 },
    announce,
  );

  const orphaned = presence === 'none' && act.draft !== null;
  useEffect(() => {
    if (!orphaned) return;
    act.close();
    announce(t(CREDIT_REFUSAL_KEYS.credits_disabled));
  }, [orphaned, act, announce, t]);

  return {
    pending: summaryOpen || (ready && (opening(ledger) || opening(schedule))),
    rereading: reopening(summary) || (ready && reopening(ledger)),
    rereadingNext: ready && reopening(schedule),
    reserved: presence !== 'none',
    view: creditBandView(
      {
        ...presenceInput,
        since: ledger.data?.pages[0]?.ledgerSince ?? null,
        upcoming: schedule.data === undefined ? null : schedule.data.map(toUpcomingSource),
        nowMs: today.getTime(),
      },
      {
        t,
        locale: i18n.language,
        zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    ),
    retry: () => {
      if (summary.isError) void summary.refetch();
      if (schedule.isError) void schedule.refetch();
      if (ledger.isError) void ledger.refetch();
    },
    retrySchedule: () => {
      void schedule.refetch();
    },
    act,
  };
}
